import oidcAuth from '@/auth/oidcAuth'
import { useAuthStore } from '@/stores/useAuthStore'
import { useBoostCorePermissionStore } from '@/stores/useBoostCorePermissionStore'

const SUITE_URL = import.meta.env.VITE_SUITE_URL as string

/**
 * Single-flight, cross-tab session recovery.
 *
 * Called from the apiFetch 401 handler. Returns true if the boostcore session
 * is now valid (the caller should retry its request); false if recovery is
 * impossible (the caller should redirect to /auth/login).
 *
 * Implementation notes (read carefully — past iterations deadlocked here):
 * - Single-flight in-tab via a module-level promise.
 * - Single-flight cross-tab via Web Locks API in exclusive mode.
 * - All HTTP calls inside the lock use raw fetch — never a wrapper that itself
 *   triggers recovery on 401, otherwise the lock would re-enter and deadlock.
 * - Auth method drives strategy:
 *     - password: try /auth/user/refresh. If it fails, give up (no silent path
 *       to re-auth a password user).
 *     - msal: try /auth/user/refresh. If it fails, signinSilent with Azure
 *       then /auth/user/login.
 */

let inflight: Promise<boolean> | null = null

export function recoverSession(): Promise<boolean> {
  if (inflight) return inflight
  inflight = (async () => {
    try {
      return await navigator.locks.request('auth-recover', { mode: 'exclusive' }, doRecover)
    } catch {
      return false
    } finally {
      inflight = null
    }
  })()
  return inflight
}

async function doRecover(): Promise<boolean> {
  // Smart early-out: if a peer tab just rotated the session, a raw /me will
  // pass and we skip rotation entirely.
  if (await pingMe()) return true

  const boostCoreStore = useBoostCorePermissionStore()
  const authMethod = boostCoreStore.authMethod

  // Try refresh first — works for both password and msal as long as the
  // session is still alive on the server. We only short-circuit if a real
  // request (pingMe) passes afterwards: a refresh can "succeed" while the
  // session is absolutely expired, in which case we must fall through to a
  // full re-login instead of dead-ending here.
  if (await rawRefresh() && await pingMe()) {
    return true
  }

  // Refresh insufficient. Password users have no silent re-auth path.
  if (authMethod === 'password') return false

  // OIDC path: silent renew Azure, then full coreLogin.
  const azureUser = await azureSilentRenew()
  if (!azureUser) return false
  if (!(await rawCoreLogin(azureUser.access_token, azureUser.profile?.oid))) return false
  return await pingMe()
}

/**
 * Bare /me probe — no recovery loop. Safe to call from inside the lock.
 */
async function pingMe(): Promise<boolean> {
  try {
    const res = await fetch(`${SUITE_URL}/auth/user/me`, {
      method: 'GET',
      credentials: 'include',
    })
    return res.ok
  } catch {
    return false
  }
}

async function rawRefresh(): Promise<boolean> {
  try {
    const csrf = useAuthStore().getCoreCsrfToken()
    const res = await fetch(`${SUITE_URL}/auth/user/refresh`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(csrf ? { 'x-csrf-token': csrf } : {}),
      },
    })
    if (!res.ok) return false
    const data = await res.json().catch(() => null) as { csrfToken?: string; expiresAt?: string } | null
    if (data?.csrfToken) useAuthStore().setCoreCsrfTokenInternal(data.csrfToken)
    return true
  } catch {
    return false
  }
}

async function azureSilentRenew(): Promise<{ access_token: string; profile: { oid?: string } } | null> {
  try {
    const user = await oidcAuth.mgr.getUser()
    // Don't attempt signinSilent without an existing OIDC user — the user
    // signed out (or never signed in via OIDC) and triggering a silent renew
    // would open a hidden Azure iframe that can only fail.
    if (!user) return null
    if (user.expired) {
      const renewed = await oidcAuth.mgr.signinSilent()
      if (!renewed) return null
      return { access_token: renewed.access_token, profile: renewed.profile as { oid?: string } }
    }
    return { access_token: user.access_token, profile: user.profile as { oid?: string } }
  } catch {
    return null
  }
}

async function rawCoreLogin(accessToken: string, oid?: string): Promise<boolean> {
  try {
    const res = await fetch(`${SUITE_URL}/auth/user/login`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token: accessToken, oid }),
    })
    if (!res.ok) return false
    const data = await res.json().catch(() => null) as { csrfToken?: string } | null
    if (data?.csrfToken) useAuthStore().setCoreCsrfTokenInternal(data.csrfToken)
    return true
  } catch {
    return false
  }
}
